/**
 * Ask — plain-language questions about the budget.
 *
 * The answers come from a Workers AI model that reads Kete's own figures through
 * read-only tools (see apps/api/src/services/ask.ts), so it can say what the forecast,
 * Fill and Cushion say, but never change anything. Each answer names the figures it
 * came from, so a surprising one can be checked on the screen it came from.
 *
 * The conversation lives in this browser tab — sessionStorage, so it survives moving
 * between screens — and is sent whole with each question. Nothing about it is stored
 * on the server.
 */

import { useMutation } from '@tanstack/react-query'
import { type FormEvent, Fragment, type ReactNode, useEffect, useRef, useState } from 'react'
import { askQuestion } from '../api.ts'

interface Turn {
  role: 'user' | 'assistant'
  content: string
  sources?: string[]
}

const STARTERS = [
  'What’s the biggest expense coming up?',
  'Where are we short?',
  'Do we have enough for the must-haves?',
  'Where can I take money from to fill things up?',
]

/** What each tool is, in the words of the screen it shares figures with. */
const SOURCE_NAMES: Record<string, string> = {
  upcoming_bills: 'scheduled bills (Ahead)',
  account_outlook: 'the forecast (Ahead)',
  shortfalls: 'what is short (Fill)',
  fill_plan: 'the Fill plan',
  needed_by: 'bills and targets due (Fill, Cushion)',
  spare_money: 'spare money (Cushion)',
  spending: 'spending (Looking back)',
  loans: 'Loans',
}

/** The API caps a conversation at twenty turns; keep the most recent. */
const MAX_TURNS = 20
const STORAGE_KEY = 'kete.ask'

/** About six lines, after which the box scrolls instead of pushing the page. */
const MAX_BOX_HEIGHT = 160

function load(): Turn[] {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '[]') as Turn[]
  } catch {
    return []
  }
}

export function Ask() {
  const [turns, setTurns] = useState<Turn[]>(load)
  const [draft, setDraft] = useState('')
  const end = useRef<HTMLDivElement>(null)
  const box = useRef<HTMLTextAreaElement>(null)

  /*
   * Grow with what is typed, then scroll inside past the cap. Measured from the content
   * rather than left to CSS `field-sizing: content`, which iPhone Safari — where this is
   * an installed app — does not reliably support yet. Clearing the height first lets it
   * shrink again when text is deleted or sent.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: the draft is what changes the height, though the effect reads the element.
  useEffect(() => {
    const el = box.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MAX_BOX_HEIGHT)}px`
    el.style.overflowY = el.scrollHeight > MAX_BOX_HEIGHT ? 'auto' : 'hidden'
  }, [draft])

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(turns))
    } catch {
      // Private browsing or storage blocked: the conversation just won't outlive the screen.
    }
    end.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [turns])

  const send = useMutation({
    mutationFn: (history: Turn[]) =>
      askQuestion({
        messages: history.slice(-MAX_TURNS).map(({ role, content }) => ({ role, content })),
      }),
    onSuccess: (response) =>
      setTurns((current) => [
        ...current,
        { role: 'assistant', content: response.answer, sources: response.sources },
      ]),
  })

  const ask = (question: string) => {
    const text = question.trim()
    if (!text || send.isPending) return
    const next: Turn[] = [...turns, { role: 'user', content: text }]
    setTurns(next)
    setDraft('')
    send.mutate(next)
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    ask(draft)
  }

  return (
    <section className="flex max-w-[760px] flex-col gap-5 px-4 pt-[22px] pb-[60px] sm:px-7">
      <div className="flex flex-col gap-1.5">
        <h2 className="m-0 font-display text-[34px] font-normal leading-none">Ask</h2>
        <p className="m-0 max-w-[600px] text-[14px] text-muted">
          Questions about the budget, answered from the same figures as the other screens. It can
          tell you what to do; it never changes anything.
        </p>
      </div>

      {turns.length === 0 && (
        <div className="flex flex-wrap gap-2">
          {STARTERS.map((starter) => (
            <button
              key={starter}
              type="button"
              onClick={() => ask(starter)}
              className="rounded-full border border-line bg-card px-3.5 py-2 text-left text-[13px] text-ink transition-colors hover:bg-sand"
            >
              {starter}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-3">
        {turns.map((turn, index) =>
          turn.role === 'user' ? (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: a conversation only ever appends, so a turn's place is its identity.
              key={index}
              className="max-w-[85%] self-end rounded-[16px] rounded-br-[6px] bg-cta px-4 py-2.5 text-[14px] text-cta-fg"
            >
              {turn.content}
            </div>
          ) : (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: a conversation only ever appends, so a turn's place is its identity.
              key={index}
              className="flex max-w-[92%] flex-col gap-1.5 self-start rounded-[16px] rounded-bl-[6px] border border-line bg-card px-4 py-3 text-[14px] leading-[1.55] [overflow-wrap:anywhere]"
            >
              <Answer text={turn.content} />
              {turn.sources && turn.sources.length > 0 && (
                <span className="text-[11.5px] text-muted">
                  From {turn.sources.map((source) => SOURCE_NAMES[source] ?? source).join(', ')}
                </span>
              )}
            </div>
          ),
        )}
        {send.isPending && (
          <div className="self-start rounded-[16px] border border-line bg-card px-4 py-3 text-[14px] text-muted">
            Looking…
          </div>
        )}
        {send.isError && (
          <div className="self-start rounded-[16px] bg-blush-soft px-4 py-3 text-[13px] text-blush-deep">
            {send.error.message}
          </div>
        )}
        <div ref={end} />
      </div>

      <form
        onSubmit={onSubmit}
        className="sticky bottom-24 flex items-end gap-2 rounded-card border border-line bg-paper p-2 shadow-[var(--shadow-pop)] lg:bottom-4"
      >
        <textarea
          ref={box}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            // Enter sends, Shift+Enter breaks the line — what every chat box does.
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              ask(draft)
            }
          }}
          rows={1}
          placeholder="Ask about the budget…"
          aria-label="Your question"
          className="min-h-[40px] flex-1 resize-none rounded-[10px] bg-transparent px-2.5 py-2 text-[14px] text-ink outline-none"
        />
        {turns.length > 0 && (
          <button
            type="button"
            onClick={() => setTurns([])}
            disabled={send.isPending}
            className="rounded-full px-3 py-2 text-[12px] text-muted hover:text-ink"
          >
            Clear
          </button>
        )}
        <button
          type="submit"
          disabled={!draft.trim() || send.isPending}
          className="rounded-full bg-cta px-4 py-2.5 text-[13px] font-medium text-cta-fg hover:bg-cta-hover disabled:opacity-40"
        >
          Ask
        </button>
      </form>
    </section>
  )
}

/**
 * The little markdown the model uses — **bold**, and lines starting "- " as a list —
 * turned into elements rather than HTML, so an answer can never inject markup.
 */
function Answer({ text }: { text: string }) {
  const blocks: ReactNode[] = []
  let list: string[] = []
  const flush = () => {
    if (list.length === 0) return
    blocks.push(
      <ul key={`list-${blocks.length}`} className="m-0 flex list-disc flex-col gap-0.5 pl-5">
        {list.map((item, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: list items are rendered once, in order.
          <li key={i}>{inline(item)}</li>
        ))}
      </ul>,
    )
    list = []
  }
  for (const line of text.split('\n')) {
    const item = /^\s*[-*•]\s+(.*)$/.exec(line)
    if (item) {
      list.push(item[1] ?? '')
      continue
    }
    flush()
    if (line.trim()) {
      blocks.push(
        <p key={`p-${blocks.length}`} className="m-0">
          {inline(line)}
        </p>,
      )
    }
  }
  flush()
  return <>{blocks}</>
}

function inline(text: string): ReactNode {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      // biome-ignore lint/suspicious/noArrayIndexKey: fragments of one line, rendered once.
      <strong key={i} className="font-medium">
        {part.slice(2, -2)}
      </strong>
    ) : (
      // biome-ignore lint/suspicious/noArrayIndexKey: fragments of one line, rendered once.
      <Fragment key={i}>{part}</Fragment>
    ),
  )
}
